/**
 * Message Input Component
 * @implements FS-002.7.9 - Conversation Input Behavior
 * @implements FS-002.7.6 - AI PM Conversation in Archived Projects
 * @implements US-020 - AI PM Conversation UI
 * @implements US-030 - Draft Message Preservation
 *
 * Provides a multiline text input for composing messages with:
 * - Enter to send, Shift+Enter for newline
 * - Disabled state for archived projects
 * - Draft preservation across navigation and page refresh (localStorage)
 */

import {
  useState,
  useCallback,
  useEffect,
  useRef,
  KeyboardEvent,
  ChangeEvent,
} from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';

/**
 * localStorage key prefix for draft messages
 * @implements US-030
 */
const DRAFT_KEY_PREFIX = 'jared_draft_';

/**
 * Debounce delay for saving drafts (ms)
 */
const DRAFT_SAVE_DELAY = 500;

/**
 * Get the localStorage key for a project's draft
 */
const getDraftKey = (projectId: string): string =>
  `${DRAFT_KEY_PREFIX}${projectId}`;

/**
 * Load draft from localStorage
 * @implements US-030 - Draft restored when returning to project
 */
const loadDraft = (projectId: string): string => {
  if (!projectId) return '';
  try {
    return localStorage.getItem(getDraftKey(projectId)) || '';
  } catch {
    return '';
  }
};

/**
 * Save draft to localStorage
 * @implements US-030 - Draft persists across page refreshes
 */
const saveDraft = (projectId: string, content: string): void => {
  if (!projectId) return;
  try {
    if (content.trim()) {
      localStorage.setItem(getDraftKey(projectId), content);
    } else {
      localStorage.removeItem(getDraftKey(projectId));
    }
  } catch {
    // Ignore localStorage errors (quota exceeded, etc.)
  }
};

/**
 * Clear draft from localStorage
 * @implements US-030 - Draft cleared after sending
 */
const clearDraft = (projectId: string): void => {
  if (!projectId) return;
  try {
    localStorage.removeItem(getDraftKey(projectId));
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * Props for MessageInput component
 */
export interface MessageInputProps {
  /** Project ID for draft persistence */
  projectId?: string;
  /** Callback when a message is sent */
  onSend: (content: string) => void | Promise<void>;
  /** Whether the input is disabled (e.g., archived project) */
  disabled?: boolean;
  /** Reason for being disabled (shown as helper text) */
  disabledReason?: string;
  /** Whether a message is currently being sent */
  isSending?: boolean;
  /** Placeholder text */
  placeholder?: string;
}

/**
 * MessageInput Component
 * @implements FS-002.7.9 - Conversation Input Behavior
 *
 * Multiline text input with keyboard shortcuts:
 * - Enter: Send message (when not empty)
 * - Shift+Enter: Insert newline
 *
 * @example
 * ```tsx
 * <MessageInput
 *   onSend={(content) => console.log('Sending:', content)}
 *   disabled={isArchived}
 *   disabledReason="This project is archived"
 * />
 * ```
 */
export const MessageInput = ({
  projectId,
  onSend,
  disabled = false,
  disabledReason,
  isSending = false,
  placeholder = 'Type your message...',
}: MessageInputProps) => {
  // Initialize message from localStorage draft
  const [message, setMessage] = useState(() =>
    projectId ? loadDraft(projectId) : '',
  );

  // Ref for debounce timer
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Load draft when projectId changes
   * @implements US-030 - Draft restored when returning to project
   */
  useEffect(() => {
    if (projectId) {
      setMessage(loadDraft(projectId));
    }
  }, [projectId]);

  /**
   * Cleanup debounce timer on unmount
   */
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  /**
   * Handle message input change with debounced draft saving
   * @implements US-030 - Draft saved on change
   */
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setMessage(newValue);

      // Debounce draft saving
      if (projectId) {
        if (saveTimerRef.current) {
          clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = setTimeout(() => {
          saveDraft(projectId, newValue);
        }, DRAFT_SAVE_DELAY);
      }
    },
    [projectId],
  );

  /**
   * Handle sending the message
   * @implements FS-002.7.9 - Empty messages cannot be sent
   * @implements US-030 - Draft cleared after sending
   */
  const handleSend = useCallback(async () => {
    const trimmedMessage = message.trim();

    // Don't send empty messages
    if (!trimmedMessage || disabled || isSending) {
      return;
    }

    // Call onSend and clear input on success
    try {
      await onSend(trimmedMessage);
      setMessage('');
      // Clear draft from localStorage on successful send
      if (projectId) {
        clearDraft(projectId);
      }
    } catch (error) {
      // Keep message in input on error so user can retry
      console.error('Failed to send message:', error);
    }
  }, [message, disabled, isSending, onSend, projectId]);

  /**
   * Handle keyboard events
   * @implements FS-002.7.9 - Enter to send, Shift+Enter for newline
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      // Enter without Shift sends the message
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
      // Shift+Enter is handled by default (inserts newline)
    },
    [handleSend],
  );

  const isInputDisabled = disabled || isSending;
  const canSend = message.trim().length > 0 && !isInputDisabled;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        backgroundColor: disabled ? 'grey.50' : 'background.paper',
      }}
    >
      {/* Disabled reason banner */}
      {disabled && disabledReason && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mb: 1,
            textAlign: 'center',
            fontStyle: 'italic',
          }}
        >
          {disabledReason}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        {/* Multiline text input */}
        <TextField
          fullWidth
          multiline
          maxRows={6}
          minRows={1}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isInputDisabled}
          placeholder={disabled ? 'Input disabled' : placeholder}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
          inputProps={{
            'aria-label': 'Message input',
          }}
        />

        {/* Send button */}
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!canSend}
          aria-label="Send message"
          sx={{
            p: 1,
            '&:hover': {
              backgroundColor: 'primary.light',
              color: 'primary.contrastText',
            },
          }}
        >
          {isSending ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <SendIcon />
          )}
        </IconButton>
      </Box>

      {/* Character hint */}
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}
      >
        Press Enter to send, Shift+Enter for new line
      </Typography>
    </Paper>
  );
};
